import { createSlice } from "@reduxjs/toolkit";
import {
  DEFAULT_TIME_ZONE_DISPLAY,
  isTimeFormatPreference,
  isTimeZoneDisplayPreference,
} from "@lemonadetraining/contracts";
import { firebaseDB, COLLECTIONS } from "./firebaseApp";
import { doc, getDoc } from "firebase/firestore";
import {
  readTimeFormatMirror,
  writeTimeFormatMirror,
  clearTimeFormatMirror,
} from "./timeFormatMirror";
import {
  readTimeZoneDisplayMirror,
  writeTimeZoneDisplayMirror,
  clearTimeZoneDisplayMirror,
} from "./timeZoneDisplayMirror";
import { grantSignalEquals } from "./grantSignal";

const userINIT = {
  user: null,
  claims: null,
  isLoggedIn: false,
  userInfoData: {},
  timeFormat: readTimeFormatMirror(),
  timeZoneDisplay: readTimeZoneDisplayMirror(),
  // True once this user's saved preferences were read (or the read failed).
  preferencesSynced: false,
  // The watched gym-grant fields of the own-Users doc; null until first read.
  grantSignal: null,
};

const userSlice = createSlice({
  name: "user",
  initialState: userINIT,
  reducers: {
    logOut(state) {
      state.user = null;
      state.claims = null;
      state.isLoggedIn = false;
      state.userInfoData = {};
      state.timeFormat = "system";
      state.timeZoneDisplay = DEFAULT_TIME_ZONE_DISPLAY;
      state.preferencesSynced = false;
      state.grantSignal = null;
      clearTimeFormatMirror();
      clearTimeZoneDisplayMirror();
    },
    setUser(state, actions) {
      if (state.user?.uid !== actions.payload?.uid) state.preferencesSynced = false;
      state.user = actions.payload;
      state.isLoggedIn = !!actions.payload;
    },
    setClaims(state, actions) {
      state.claims = actions.payload;
    },
    setUserInfoData(state, actions) {
      state.userInfoData = { ...actions.payload };
    },
    // Equal signals keep the previous object, so a consumer watching this by
    // reference is woken only by a real grant change, never by a re-snapshot.
    setGrantSignal(state, actions) {
      if (!grantSignalEquals(state.grantSignal, actions.payload)) {
        state.grantSignal = actions.payload;
      }
    },
    setTimeFormat(state, actions) {
      const next = isTimeFormatPreference(actions.payload) ? actions.payload : "system";
      state.timeFormat = next;
      writeTimeFormatMirror(next, state.user?.uid);
    },
    setPreferencesSynced(state, actions) {
      state.preferencesSynced = actions.payload === true;
    },
    setTimeZoneDisplay(state, actions) {
      const next = isTimeZoneDisplayPreference(actions.payload)
        ? actions.payload
        : DEFAULT_TIME_ZONE_DISPLAY;
      state.timeZoneDisplay = next;
      writeTimeZoneDisplayMirror(next, state.user?.uid);
    },
  },
});

export const userActions = userSlice.actions;
export default userSlice;

/* ==========================
 * Async actions
 * ========================= */

/*
 * Load user Info Database
 */
export const loadUserInfoData = (userUID) => {
  return async (dispatch) => {
    const userRef = doc(firebaseDB, COLLECTIONS.Users, userUID);
    const docSnap = await getDoc(userRef);
    dispatch(userActions.setUserInfoData(docSnap.data()));
  };
};
